import React, { useState, useEffect, useMemo } from 'react';
import { Container, Form, PlaceholderButton } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useGetCountries } from '../../shared/api/queries/getCountries';
import { SelectMultiple } from '../../shared/ui/SelectMultiple/SelectMultiple';
import { apiRequests } from '../../shared/api/apiRequests';
import { toast } from 'react-toastify';
import { Select } from '../../shared/ui/Select/ui/Select';
import { USER_ROLES } from '../../Pages/Roles';

export const AccessTypeOfUser = ({ initialData, userId, setData }) => {
  const [accessCountries, setAccessCountries] = useState({
    availableCountries: [],
    forbiddenCountries: [],
    isAllCountriesAccess: false,
  });
  const [accessLevels, setAccessLevels] = useState({
    role: 2, // Добавляем роль в состояние
    reviewsAccess: { AccessLevel: 0 },
    hrAccess: { AccessLevel: 0 },
    shopAccess: { AccessLevel: 0 },
    outstaffAccess: { AccessLevel: 0 },
    contentAccessTypes: [],
  });

  // Состояние для хранения выбранных типов сущностей в блоке "Другие"
  const [selectedContentTypes, setSelectedContentTypes] = useState([]);
  const { data, isLoading } = useGetCountries();

  const countriesData = useMemo(() => {
    if (data) {
      return data.items.map((i) => ({
        label: i.title,
        value: i.id,
      }));
    }
  }, [data]);

  // Полный список типов сущностей для выпадающего списка
  const contentTypeOptions = [
    { label: 'Все разделы', value: 1 },
    { label: 'Новостные записи', value: 2 },
    { label: 'Мы в СМИ', value: 3 },
    { label: 'Портфолио', value: 4 },
    { label: 'Комплаенс', value: 5 },
    { label: 'События', value: 6 },
    { label: 'Команда', value: 7 },
    { label: 'Основное', value: 8 },
    { label: 'Партнеры', value: 9 },
  ];

  // Инициализация изначальных значений
  useEffect(() => {
    if (initialData) {
      setAccessLevels({
        role: initialData.role || initialData.Role || 2, // Добавляем роль
        reviewsAccess: initialData.reviewsAccess || initialData.ReviewsAccess,
        hrAccess: initialData.hrAccess || initialData.HRAccess,
        shopAccess: initialData.shopAccess || initialData.ShopAccess,
        outstaffAccess: initialData.outstaffAccess || initialData.OutstaffAccess,
        contentAccessTypes: initialData.contentAccessTypes || initialData.ContentAccessTypes || [],
      });
      setAccessCountries({
        isAllCountriesAccess: initialData.isAllCountriesAccess || initialData.IsAllCountriesAccess,
        availableCountries: initialData.availableCountries || initialData.AvailableCountriesIds || [],
        forbiddenCountries: initialData.forbiddenCountries || initialData.ForbiddenCountriesIds || [],
      });
      
      // Инициализируем выбранные типы сущностей
      if (initialData.contentAccessTypes && initialData.contentAccessTypes.length > 0) {
        const selectedTypes = initialData.contentAccessTypes.map(item => item.Type || item.type);
        setSelectedContentTypes(selectedTypes);
      } else if (initialData.ContentAccessTypes && initialData.ContentAccessTypes.length > 0) {
        const selectedTypes = initialData.ContentAccessTypes.map(item => item.Type || item.type);
        setSelectedContentTypes(selectedTypes);
      }
    }
  }, [initialData]);

  const maxLevels = {
    ReviewsAccess: 3,
    HRAccess: 7,
    ShopAccess: 9,
    OutstaffAccess: 6,
  };

  const handleCheck = (entity, level) => {
    setAccessLevels((prev) => {
      // Получаем текущий уровень доступа, учитывая возможные разные имена полей
      const currentLevel = prev[entity].accessLevel || prev[entity].AccessLevel || 0;

      let newLevel;
      if (currentLevel === level) {
        // Если текущий уровень совпадает, понижаем его на 1 или обнуляем
        newLevel = currentLevel > 1 ? currentLevel - 1 : 0;
      } else {
        // Устанавливаем новый уровень
        newLevel = level;
      }

      // Создаем объект с правильными именами полей для API
      const updatedLevels = {
        ...prev,
        [entity]: { 
          AccessLevel: newLevel, 
          id: prev[entity].id || prev[entity].Id 
        },
      };
      
      // Обновляем данные в родительском компоненте
      if (setData) {
        setData({
          ...accessCountries,
          ...updatedLevels,
          // Преобразуем имена полей для API
          Role: updatedLevels.role,
          ReviewsAccess: updatedLevels.reviewsAccess,
          HRAccess: updatedLevels.hrAccess,
          ShopAccess: updatedLevels.shopAccess,
          OutstaffAccess: updatedLevels.outstaffAccess,
          IsAllCountriesAccess: accessCountries.isAllCountriesAccess,
          AvailableCountriesIds: accessCountries.availableCountries,
          ForbiddenCountriesIds: accessCountries.forbiddenCountries
        });
      }
      
      return updatedLevels;
    });
  };

  // Эта функция больше не используется, так как мы обрабатываем каждую сущность отдельно

  // Добавление выбранных типов в contentAccessTypes при выборе новых сущностей
  const handleContentTypeChange = (selectedValues) => {
    // Используем setTimeout для предотвращения вызова setState во время рендеринга
    setTimeout(() => {
      // Обновляем список выбранных типов
      setSelectedContentTypes(selectedValues);
      
      // Сохраняем существующие уровни доступа для уже выбранных типов
      const existingAccessTypes = accessLevels.contentAccessTypes || [];
      
      // Создаем обновленный массив с сохранением существующих уровней
      const updatedContentAccessTypes = selectedValues.map(type => {
        // Проверяем, есть ли уже доступ для этого типа
        const existingAccess = existingAccessTypes.find(item => item.Type === type || item.type === type);
        
        // Если есть, сохраняем уровень, иначе ставим по умолчанию 1
        return {
          AccessLevel: existingAccess ? (existingAccess.AccessLevel || existingAccess.accessLevel || 1) : 1,
          Type: type
        };
      });
      
      setAccessLevels(prev => ({
        ...prev,
        contentAccessTypes: updatedContentAccessTypes
      }));
      
      // Обновляем данные в родительском компоненте
      if (setData) {
        setData(prev => ({
          ...prev,
          contentAccessTypes: updatedContentAccessTypes
        }));
      }
    }, 0);
  };
  
  // Обновление уровня доступа для конкретной сущности
  const handleEntityAccessLevelChange = (entityType, level) => {
    // Используем setTimeout для предотвращения вызова setState во время рендеринга
    setTimeout(() => {
      setAccessLevels(prev => {
        // Находим текущий доступ для сущности
        const contentAccessTypes = [...prev.contentAccessTypes];
        const entityIndex = contentAccessTypes.findIndex(item => item.Type === entityType || item.type === entityType);
        
        if (entityIndex !== -1) {
          const currentLevel = contentAccessTypes[entityIndex].AccessLevel || contentAccessTypes[entityIndex].accessLevel || 0;
          
          // Если уровень совпадает, уменьшаем его, иначе устанавливаем новый
          let newLevel;
          if (currentLevel === level) {
            newLevel = currentLevel > 1 ? currentLevel - 1 : 0;
          } else {
            newLevel = level;
          }
          
          // Обновляем уровень доступа
          contentAccessTypes[entityIndex] = {
            ...contentAccessTypes[entityIndex],
            AccessLevel: newLevel,
            Type: entityType
          };
        }
        
        // Обновляем данные в родительском компоненте
        if (setData) {
          setData(prevData => ({
            ...prevData,
            contentAccessTypes
          }));
        }
        
        return {
          ...prev,
          contentAccessTypes
        };
      });
    }, 0);
  };

  // Этот useEffect больше не нужен, так как мы обновляем contentAccessTypes напрямую в handleContentTypeChange

  // Рендеринг чекбоксов для разных уровней доступа
  const renderCheckboxes = (entity, labels, currentLevel, onChange) => {
    // Получаем уровень доступа, учитывая возможные разные имена полей
    const level = typeof currentLevel === 'object' ? 
      (currentLevel.AccessLevel || currentLevel.accessLevel || 0) : 
      currentLevel || 0;
      
    return labels.map((label, index) => {
      const checkLevel = index + 1;
      return (
        <Form.Check
          key={`${entity}-${checkLevel}`}
          type="checkbox"
          id={`${entity}-${checkLevel}`}
          label={label}
          checked={level >= checkLevel}
          onChange={() => onChange(checkLevel)}
        />
      );
    });
  };

  return (
    <Container>
      {/* Добавляем выбор роли в блок доступов */}
      <h6 className="mt-3">Роль пользователя</h6>
      <div className="mb-2">
        <small className="text-muted">
          <ul className="ps-3 mb-2">
            <li><strong>2 - Администратор</strong>: Пользователь с доступом в администраторскую панель</li>
            <li><strong>3 - Супер-Администратор</strong>: Суперпользователь, который может делать все в рамках доступных ему стран</li>
            <li><strong>4 - Владелец</strong>: Владелец сайта с полными правами</li>
          </ul>
        </small>
      </div>
      <Select
        label="Роль пользователя"
        value={accessLevels.role}
        onChange={(value) => {
          setAccessLevels(prev => ({ ...prev, role: value }));
          setData(prev => ({ 
            ...prev, 
            role: value,
            Role: value // Добавляем поле с заглавной буквы для API
          }));
        }}
        options={[
          { value: 2, label: 'Администратор - доступ к админке с ограниченными правами' },
          { value: 3, label: 'Супер-администратор - полные права в рамках доступных стран' },
          { value: 4, label: 'Владелец - полный доступ ко всему сайту' }
        ]}
      />
      
      <h6 className="mt-3">Страны</h6>
      <Form.Check
        className={'mb-2'}
        label={'Доступ ко всем странам'}
        checked={accessCountries.isAllCountriesAccess}
        onChange={(e) => {
          const newValue = !accessCountries.isAllCountriesAccess;
          setAccessCountries((prev) => {
            const updatedCountries = {
              ...prev,
              isAllCountriesAccess: newValue,
            };
            
            // Обновляем данные в родительском компоненте
            if (setData) {
              setData({
                ...updatedCountries,
                ...accessLevels
              });
            }
            
            return updatedCountries;
          });
        }}
      />
      {isLoading ? (
        <div className="d-flex flex-column gap-3 w-25">
          <PlaceholderButton variant="secondary" />
          <PlaceholderButton variant="secondary" />
        </div>
      ) : (
        <>
          <SelectMultiple
            className={'mb-3'}
            label={'Разрешённые страны'}
            options={countriesData}
            initialValues={accessCountries.availableCountries}
            onDelete={(value) =>
              setAccessCountries((prev) => {
                const updatedCountries = {
                  ...prev,
                  availableCountries: prev.availableCountries.filter(
                    (i) => Number(i) !== Number(value),
                  ),
                };
                
                // Обновляем данные в родительском компоненте
                if (setData) {
                  setData({
                    ...updatedCountries,
                    ...accessLevels
                  });
                }
                
                return updatedCountries;
              })
            }
            onAdd={(value) =>
              setAccessCountries((prev) => {
                const updatedCountries = {
                  ...prev,
                  availableCountries: [...prev.availableCountries, value],
                };
                
                // Обновляем данные в родительском компоненте
                if (setData) {
                  setData({
                    ...updatedCountries,
                    ...accessLevels
                  });
                }
                
                return updatedCountries;
              })
            }
          />
          <SelectMultiple
            label={'Запрещённые страны'}
            options={countriesData}
            initialValues={accessCountries.forbiddenCountries}
            onDelete={(value) =>
              setAccessCountries((prev) => {
                const updatedCountries = {
                  ...prev,
                  forbiddenCountries: prev.forbiddenCountries.filter(
                    (i) => Number(i) !== Number(value),
                  ),
                };
                
                // Обновляем данные в родительском компоненте
                if (setData) {
                  setData({
                    ...updatedCountries,
                    ...accessLevels
                  });
                }
                
                return updatedCountries;
              })
            }
            onAdd={(value) =>
              setAccessCountries((prev) => {
                const updatedCountries = {
                  ...prev,
                  forbiddenCountries: [...prev.forbiddenCountries, value],
                };
                
                // Обновляем данные в родительском компоненте
                if (setData) {
                  setData({
                    ...updatedCountries,
                    ...accessLevels
                  });
                }
                
                return updatedCountries;
              })
            }
          />
        </>
      )}
      <h6 className="mt-3">Отзывы</h6>
      {renderCheckboxes(
        'reviewsAccess',
        ['Просмотр', 'Сокрытие', 'Удаление'],
        accessLevels.reviewsAccess,
        (level) => handleCheck('reviewsAccess', level),
      )}

      <h6 className="mt-3">HR</h6>
      {renderCheckboxes(
        'hrAccess',
        [
          'Просмотр вакансий',
          'Просмотр резюме',
          'Ответ на резюме',
          'Редактирование вакансий',
          'Редактирование локализации',
          'Создание вакансий',
          'Удаление вакансий',
        ],
        accessLevels.hrAccess,
        (level) => handleCheck('hrAccess', level),
      )}

      <h6 className="mt-3">Магазин</h6>
      {renderCheckboxes(
        'shopAccess',
        [
          'Просмотр товаров',
          'Просмотр заказов',
          'Принятие заказов',
          'Отмена заказов',
          'Редактирование товаров',
          'Редактирование локализации',
          'Редактирование цены',
          'Создание товаров',
          'Удаление товаров',
        ],
        accessLevels.shopAccess,
        (level) => handleCheck('shopAccess', level),
      )}

      <h6 className="mt-3">Аутстафф</h6>
      {renderCheckboxes(
        'outstaffAccess',
        [
          'Просмотр',
          'Создание',
          'Удаление',
          'Редактирование цены',
          'Редактирование текстов',
          'Редактирование локализации',
        ],
        accessLevels.outstaffAccess,
        (level) => handleCheck('outstaffAccess', level),
      )}

      <h6 className="mt-3">Другие</h6>
      {/* Заменяем одиночный выбор на множественный */}
      <SelectMultiple
        className="mb-3"
        label="Выберите сущности"
        options={contentTypeOptions}
        initialValues={selectedContentTypes}
        onAdd={(value) => {
          const valueNum = Number(value);
          // Создаем новый массив, чтобы избежать мутаций во время рендеринга
          const newSelectedTypes = [...selectedContentTypes];
          // Проверяем, что значение еще не добавлено
          if (!newSelectedTypes.includes(valueNum)) {
            newSelectedTypes.push(valueNum);
            handleContentTypeChange(newSelectedTypes);
          }
        }}
        onDelete={(value) => {
          const valueNum = Number(value);
          const newSelectedTypes = selectedContentTypes.filter(type => type !== valueNum);
          handleContentTypeChange(newSelectedTypes);
        }}
      />
      
      {/* Отображаем уровни доступа для каждой выбранной сущности */}
      {selectedContentTypes.length > 0 && (
        <div className="mb-3">
          {selectedContentTypes.map(typeId => {
            const typeOption = contentTypeOptions.find(option => option.value === typeId);
            const typeName = typeOption ? typeOption.label : `Тип ${typeId}`;
            // Исправляем доступ к полям, учитывая оба формата имен (camelCase и PascalCase)
            const contentItem = accessLevels.contentAccessTypes.find(
              item => (item.Type === typeId || item.type === typeId)
            );
            const accessLevel = contentItem ? (contentItem.AccessLevel || contentItem.accessLevel || 0) : 0;
            
            return (
              <div key={typeId} className="mb-3 border-bottom pb-3">
                <h6>{typeName}</h6>
                {renderCheckboxes(
                  `contentAccess-${typeId}`,
                  ['Просмотр', 'Создание', 'Редактирование', 'Удаление', 'Редактирование локализации'],
                  accessLevel,
                  (level) => handleEntityAccessLevelChange(typeId, level)
                )}
              </div>
            );
          })}
        </div>
      )}
    </Container>
  );
};

// import {useGetCountries} from "../../shared/api/queries/getCountries.js";
// import {SelectMultiple} from "../../shared/ui/SelectMultiple/SelectMultiple.jsx";
// import {useMemo} from "react";
// import {Form} from "react-bootstrap";
// import {ContentAccess} from "./ContentAccess.jsx";

// export const AccessTypeOfUser = ({ formData, setData }) => {
//     const { data, isLoading } = useGetCountries()

//     const countriesData = useMemo(() => {
//         if (data) {
//             return data.items.map((i) => ({
//                 label: i.title,
//                 value: i.id
//             }))
//         }
//     }, [data])

//     if (isLoading) return;

//     return (
//         <Form>
//             <h5 className={'mb-3'}>Доступы:</h5>
//             <Form.Group>
//                 <h6>Страны</h6>
//                 <Form.Check className={'mb-2'} label={'Доступ ко всем странам'} />
//                 <SelectMultiple
//                     className={'mb-3'}
//                     label={'Разрешённые страны'}
//                     options={countriesData}
//                 />
//                 <SelectMultiple
//                     label={'Запрещённые страны'}
//                     options={countriesData}
//                 />
//             </Form.Group>

//             <Form.Group className={'mt-3'}>
//                 <h6>Отзывы</h6>
//                 <Form.Check label={'Просмотр'} />
//                 <Form.Check label={'Скрытие'} />
//                 <Form.Check label={'Удаление'} />
//             </Form.Group>

//             <Form.Group className={'mt-3'}>
//                 <h6>HR</h6>
//                 <Form.Check label={'Просмотр вакансий'} />
//                 <Form.Check label={'Просмотр резюме'} />
//                 <Form.Check label={'Ответ на резюме'} />
//                 <Form.Check label={'Редактирование вакансий'} />
//                 <Form.Check label={'Редактирование локализации'} />
//                 <Form.Check label={'Создание вакансий'} />
//                 <Form.Check label={'Удаление вакансий'} />
//             </Form.Group>

//             <Form.Group className={'mt-3'}>
//                 <h6>Магазин</h6>
//                 <Form.Check label={'Просмотр товаров'} />
//                 <Form.Check label={'Просмотр заказов'} />
//                 <Form.Check label={'Принятие заказов'} />
//                 <Form.Check label={'Отмена заказов'} />
//                 <Form.Check label={'Редактирование товаров'} />
//                 <Form.Check label={'Редактирование локализации'} />
//                 <Form.Check label={'Редактирование цены'} />
//                 <Form.Check label={'Создание товаров'} />
//                 <Form.Check label={'Удаление товаров'} />
//             </Form.Group>

//             <Form.Group className={'mt-3'}>
//                 <h6>Аутстафф</h6>
//                 <Form.Check label={'Просмотр'} />
//                 <Form.Check label={'Создание'} />
//                 <Form.Check label={'Удаление'} />
//                 <Form.Check label={'Редактирование цены'} />
//                 <Form.Check label={'Редактирование текстов'} />
//                 <Form.Check label={'Редактирование локализации'} />
//             </Form.Group>

//             <Form.Group className={'mt-3'}>
//                 <h6 className={'mb-3'}>Другие</h6>
//                 <ContentAccess
//                     initialValue={formData.contentAccessTypes}
//                 />
//             </Form.Group>
//         </Form>
//     )
// }
